import { createApi } from '@reduxjs/toolkit/query/react';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { authQuery } from '../config/query.config';
import { cookieService } from '@lib/cookie.service';
import { QUERY } from '@constants/query.constants';
import { IImageUploadResponse } from './types';
import {
  IBadgeNormalizedResponse,
  IBadgeQueryParam,
  IBadgeResponse,
  ICollaborationStatus,
  IGetBadgesResponse,
  IGenerateClaimCodePayload,
  IGetBadgesResult,
  IBadgeItem,
  ILockBadgePayload,
  ILockBadgeResponse,
  IClaimCodeStatisticsParams,
  IClaimCodeStatisticsResponse,
} from 'app/(app)/dashboard/badges/badges.typings';
import { formatBadgeResponse } from 'app/(app)/dashboard/badges/badges.utils';
import { IBadgePayload, IBadgeUpdatePayload } from 'app/(app)/create-badge/create-badge.types';
import { BadgeType, DATE_FORMATS } from '@ui/constants';
import dayjs from 'dayjs';
import { IGetFanBadgesResponse } from 'app/(app)/fan-profile/fan-profile.typings';

type UploadBadgeImagePayload =
  | File
  | {
      image: File;
      includeOriginal?: boolean;
      doubleImage?: boolean;
    };

export const badgeAdapter = createEntityAdapter({
  selectId: (item: any) => item.id,
});

export const badgeSelector = badgeAdapter.getSelectors();

export const badgesApi = createApi({
  reducerPath: 'badgesApi',
  baseQuery: authQuery,
  tagTypes: [QUERY.badges, QUERY.badgeItem, QUERY.infiniteBadges],
  endpoints: (builder) => ({
    getBadges: builder.query<IGetBadgesResult, IBadgeQueryParam>({
      query: ({ page, pageSize }) => {
        const userId = cookieService.getUserId();
        return {
          url: `/badges/author/${userId}`,
          params: { page, pageSize },
        };
      },
      transformResponse: (data: IGetBadgesResponse) => {
        return {
          badges: data.badges.map(formatBadgeResponse),
          pagination: {
            pages: data.badge_pages.pages,
          },
        };
      },
      providesTags: [QUERY.badges],
    }),
    // Community-type badges authored by the creator, used to pick the crew's public-membership badge.
    // We fetch the author's badges and filter to Community client-side: the server's badgeType
    // filter can miss string-vs-number type mismatches, and excludeDraft drops badges whose
    // `draft` field is unset (e.g. the auto-generated community badge).
    getCommunityBadges: builder.query<IBadgeItem[], void>({
      query: () => {
        const userId = cookieService.getUserId();
        return {
          url: `/badges/author/${userId}`,
          // Pagination is zero-based server-side (skip = page * pageSize), so the first page is 0.
          params: { page: 0, pageSize: 100 },
        };
      },
      transformResponse: (data: IGetBadgesResponse) =>
        data.badges
          .map(formatBadgeResponse)
          .filter((badge) => Number(badge.type) === BadgeType.Community),
      providesTags: [QUERY.badges],
    }),
    infiniteBadges: builder.query<IBadgeNormalizedResponse, IBadgeQueryParam>({
      query: ({
        page,
        pageSize,
        badgeName,
        badgeType,
        excludeDraft,
        unclaimed,
        claimCodesActive,
      }) => {
        const userId = cookieService.getUserId();
        return {
          url: `/badges/author/${userId}`,
          params: {
            page,
            pageSize,
            ...(badgeName && { badgeName }),
            ...(excludeDraft && { excludeDraft }),
            ...(unclaimed && { unclaimed }),
            ...(claimCodesActive && { claimCodesActive }),
            badgeType,
          },
        };
      },
      providesTags: [QUERY.infiniteBadges],
      transformResponse: (data: IGetBadgesResponse) => {
        return {
          badges: badgeAdapter.setAll(
            badgeAdapter.getInitialState(),
            data.badges.map(formatBadgeResponse),
          ),
          pagination: {
            pages: data.badge_pages.pages,
          },
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSamePage = currentArg?.page === previousArg?.page;
        const isSameSearchKeyword = currentArg?.badgeName === previousArg?.badgeName;
        const isSameType = currentArg?.badgeType === previousArg?.badgeType;
        const claimCodes = currentArg?.claimCodesActive === previousArg?.claimCodesActive;

        return !isSamePage || !isSameSearchKeyword || isSameType || !claimCodes;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${queryArgs?.badgeName}-${queryArgs?.badgeType}-${queryArgs?.claimCodesActive}`,
      merge: (currentState, incomingState) => {
        badgeAdapter.addMany(currentState.badges, badgeSelector.selectAll(incomingState.badges));
        currentState.pagination = incomingState.pagination;
      },
    }),
    getBadgeById: builder.query({
      query: (id) => `/badges/${id}?id=${id}`,
      transformResponse: formatBadgeResponse,
      providesTags(result) {
        return [{ type: QUERY.badgeItem, id: result?.id }];
      },
    }),
    deleteBadgeById: builder.mutation<void, string>({
      query: (id) => ({
        url: `/badges/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [QUERY.badges],
    }),
    updateBadgeById: builder.mutation<void, IBadgeUpdatePayload>({
      query: ({ id, expired, sponsors }) => ({
        url: `/badges/${id}`,
        method: 'PATCH',
        body: {
          expired,
          sponsors,
        },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: QUERY.badgeItem, id }],
    }),
    uploadBadgeImage: builder.mutation({
      query: (payload: UploadBadgeImagePayload) => {
        const image = payload instanceof File ? payload : payload.image;
        const includeOriginal = payload instanceof File ? false : Boolean(payload.includeOriginal);
        const doubleImage = payload instanceof File ? true : payload.doubleImage !== false;
        const form = new FormData();
        form.append('file', image);
        const queryParams = new URLSearchParams();
        if (doubleImage) {
          queryParams.set('double_image', 'true');
        }
        if (includeOriginal) {
          queryParams.set('original', 'true');
        }
        return {
          url: `/badge-upload?${queryParams.toString()}`,
          method: 'POST',
          body: form,
          formData: true,
        };
      },
      transformResponse(data: IImageUploadResponse) {
        return data;
      },
    }),
    createBadge: builder.mutation({
      query: (badge: IBadgePayload) => {
        const userId = cookieService.getUserId();

        const claimCodesMap: Record<BadgeType, object> = {
          [BadgeType.Prize]: {
            claim_codes_active: false,
          },
          [BadgeType.Engagement]: {
            claim_codes_active: false,
          },
          [BadgeType.Community]: {
            claim_codes_active: badge.claimCode,
          },
          [BadgeType.Purchase]: {
            claim_codes_active: true,
          },
        };
        return {
          url: `/badges/${badge.id ?? ''}${badge.lock ? `?unlockKey=${badge.lock}` : ''}`,
          method: badge.id ? 'PATCH' : 'POST',
          body: {
            name: badge.name,
            author: userId as string,
            quantity: badge.amount || 0,
            external_url: badge.imageUrl,
            larger_image_url: badge.largeImageUrl,
            description: badge.claimCodeData.description,
            availability: badge.availability || 0,
            media_source: badge.platform,
            type: badge.type,
            collaboration_enabled: badge.collaboration,
            collaboration_status: badge.collaboration_status,
            draft: badge.draft,
            custom_badge_color: badge.color,
            public_link: badge.publicLink,
            perquisites: badge.perks,
            caption: badge.caption,
            allowedDomain: badge.allowedDomain,
            message: badge.message,
            shopify_image_url: badge.shopify_image_url,
            ...claimCodesMap[badge.type],
            claim_codes: badge.claimCodeData,
          },
        };
      },
      transformResponse: (response: { _id: string }) => {
        return response._id;
      },
      invalidatesTags(id) {
        return [QUERY.badges, { type: QUERY.badgeItem, id }];
      },
    }),
    updateCollaborationStatus: builder.mutation<
      string,
      { id: string; status: ICollaborationStatus }
    >({
      query: ({ id, status }) => {
        return {
          url: `/badges/updateCollaborationStatus/${id}`,
          method: 'PATCH',
          body: {
            status,
          },
        };
      },
      transformResponse: (response: IBadgeResponse) => {
        return response._id;
      },
      invalidatesTags(id) {
        return [QUERY.badges, { type: QUERY.badgeItem, id }];
      },
    }),

    updateShopifyImageUrl: builder.mutation<
      string,
      { id: string; shopify_image_url?: string; gifframe_image_url?: string }
    >({
      query: ({ id, shopify_image_url, gifframe_image_url }) => {
        const body: Record<string, string> = {};
        if (typeof shopify_image_url !== 'undefined') {
          body.shopify_image_url = shopify_image_url;
        }
        if (typeof gifframe_image_url !== 'undefined') {
          body.gifframe_image_url = gifframe_image_url;
        }
        return {
          url: `/badges/${id}`,
          method: 'PATCH',
          body,
        };
      },
      transformResponse: (response: IBadgeResponse) => {
        return response._id;
      },
      invalidatesTags(id) {
        return [QUERY.badges, { type: QUERY.badgeItem, id }];
      },
    }),

    addCollaboration: builder.mutation({
      query(payload) {
        return {
          url: `/badges/collaboration/${payload.id}`,
          method: 'PATCH',
          body: {
            collaboratorId: payload.collaboratorId,
            badgeId: payload.id,
          },
        };
      },
      transformResponse: (response: IBadgeResponse) => {
        return {
          id: response._id,
          name: response.name,
        };
      },
      invalidatesTags(response) {
        if (response && response.id) {
          return [QUERY.badges, { type: QUERY.badgeItem, id: response.id }];
        }
        return [];
      },
    }),
    getBadgeClaimCodes: builder.mutation<{ success: boolean }, { id: string; name: string }>({
      queryFn: async ({ id, name }, api, extraOptions, baseQuery) => {
        const response = await baseQuery({
          url: `/claim-codes/${id}`,
          responseHandler: (response) => response.blob() as Blob,
        });
        if (response.data) {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;
          const blobURL = url.createObjectURL(response.data as Blob);
          hiddenElement.href = blobURL;
          hiddenElement.target = '_blank';
          hiddenElement.download = `KOR-LINK-CODES__${name}__${dayjs().format(
            DATE_FORMATS.iso,
          )}.csv`;
          hiddenElement.click();
          url.revokeObjectURL(blobURL);
          hiddenElement.remove();
          return {
            data: {
              success: true,
            },
          };
        }
        return {
          data: {
            success: false,
          },
        };
      },
    }),
    getFanBadgesInfinite: builder.query({
      query({ creatorId, fanId, page, pageSize }) {
        return {
          url: `/badges/badges-by-company/${fanId}?author=${creatorId}&page=${page}&pageSize=${pageSize}`,
        };
      },
      transformResponse({ byCompany, badge_pages }: IGetFanBadgesResponse) {
        const badges = badgeAdapter.addMany(
          badgeAdapter.getInitialState(),
          byCompany.map(formatBadgeResponse),
        );

        return {
          pages: badge_pages.pages,
          badgeAmount: badge_pages.total_values,
          badges,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameCreator = currentArg.creatorId === previousArg?.creatorId;
        const isSameFan = currentArg.fanId === previousArg?.fanId;
        const isSamePage = currentArg.page === previousArg?.page;
        return !isSameCreator || !isSamePage || !isSameFan;
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}-${queryArgs.creatorId}-${queryArgs.fanId}`;
      },
      merge(currentState, incomingState) {
        badgeAdapter.addMany(currentState.badges, badgeSelector.selectAll(incomingState.badges));
      },
    }),
    lockBadge: builder.mutation<ILockBadgeResponse, ILockBadgePayload>({
      query: ({ id, newLock = false }) => ({
        url: `/lock-badge/${id}`,
        method: 'PUT',
        body: { newLock },
      }),
      transformResponse: (response: ILockBadgeResponse) => response,
      invalidatesTags: (result, error, { id }) => [{ type: QUERY.badgeItem, id }],
    }),
    triggerCommunityBadgeCreate: builder.mutation<any, void>({
      query: () => ({
        url: `/badge/auto-badge-trigger`,
        method: 'POST',
      }),
      invalidatesTags: [QUERY.badges],
    }),
    generateClaimCode: builder.mutation<any, IGenerateClaimCodePayload>({
      query: ({ badgeId, batchQuantity, batchDescription }) => ({
        url: `/claim-codes/${badgeId}`,
        method: 'PUT',
        body: { batchQuantity, description: batchDescription },
      }),
      invalidatesTags: (result, error, { badgeId }) => [{ type: QUERY.badgeItem, id: badgeId }],
    }),
    generateClaimCodePDF: builder.mutation<{ message: string; total_codes: number }, string>({
      query: (badgeId) => ({
        url: `public/v1/generate_claim_code_pdf/${badgeId}`,
        method: 'GET',
      }),
      invalidatesTags: (result, error, badgeId) => [{ type: QUERY.badgeItem, id: badgeId }],
    }),
    getClaimCodeStatistics: builder.query<IClaimCodeStatisticsResponse, IClaimCodeStatisticsParams>(
      {
        query: ({ badgeId, batch }) => ({
          url: `/claim-codes-statistics/${badgeId}`,
          params: {
            ...(batch && { batch }),
          },
        }),
        providesTags: (result, error, { badgeId }) => [{ type: QUERY.badgeItem, id: badgeId }],
      },
    ),
  }),
});

export const {
  useGetBadgesQuery,
  useGetCommunityBadgesQuery,
  useGetBadgeByIdQuery,
  useLazyGetBadgeByIdQuery,
  useCreateBadgeMutation,
  useUploadBadgeImageMutation,
  useDeleteBadgeByIdMutation,
  useUpdateShopifyImageUrlMutation,
  useUpdateCollaborationStatusMutation,
  useAddCollaborationMutation,
  useGetBadgeClaimCodesMutation,
  useGetFanBadgesInfiniteQuery,
  useInfiniteBadgesQuery,
  useLazyInfiniteBadgesQuery,
  useUpdateBadgeByIdMutation,
  useLockBadgeMutation,
  useTriggerCommunityBadgeCreateMutation,
  useGenerateClaimCodeMutation,
  useGenerateClaimCodePDFMutation,
  useGetClaimCodeStatisticsQuery,
} = badgesApi;
